"use client";

import { useRef, useState } from "react";

/** @deprecated Sliders now have symmetric padding — use items-center instead of items-end */
export const DROPDOWN_VALIGN = 0;

/** Parse a JSON points string for CurveSlider, falling back to a default array.
 *  Returns a sorted-by-x array of [x, y] pairs. */
export function parsePoints(json: string | undefined, fallback: [number, number][]): [number, number][] {
  if (json) {
    try {
      const arr = JSON.parse(json);
      if (Array.isArray(arr) && arr.length >= 1 && arr.every((v: unknown) => Array.isArray(v) && v.length === 2 && typeof v[0] === "number" && typeof v[1] === "number"))
        return (arr as [number, number][]).sort((a, b) => a[0] - b[0]);
    } catch {}
  }
  return fallback;
}

/** Single-thumb slider with optional clickable reject zones on either side of the thumb. */
export function Slider({ min, max, step, value, onChange, fmt, disabled, trackCls, rejectLo, rejectHi, onRejectLoToggle, onRejectHiToggle }: {
  min: number; max: number; step: number; value: string;
  onChange: (v: string) => void; fmt?: (v: string) => string; disabled?: boolean; trackCls?: string;
  rejectLo?: boolean; rejectHi?: boolean; onRejectLoToggle?: () => void; onRejectHiToggle?: () => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  const decimals = step.toString().split(".")[1]?.length ?? 0;
  const snap = (v: number) => Number((Math.round(v / step) * step).toFixed(decimals));

  const raw = Number(value);
  const num = Math.max(min, Math.min(max, isNaN(raw) ? min : raw));

  const valFromX = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return snap(min + pct * (max - min));
  };

  const pctFor = (v: number) => ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * 100;

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).focus();
    const onMove = (ev: MouseEvent) => onChange(String(valFromX(ev.clientX)));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") delta = step;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") delta = -step;
    else return;
    e.preventDefault();
    const v = snap(num + delta);
    if (v >= min && v <= max) onChange(String(v));
  };

  const f = (v: number) => fmt ? fmt(String(v)) : String(v);
  const pct = pctFor(num);

  return (
    <div className={trackCls || "flex-1 min-w-[3rem]"}>
      <div className={`text-xs font-mono text-center mb-0.5 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(num)}</div>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(min)}</span>
        <div ref={trackRef} className={`relative h-3.5 flex-1 ${disabled ? "pointer-events-none" : ""}`}>
          <div className="absolute top-[5px] left-0 right-0 h-1 rounded-sm" style={{ background: disabled ? "#262626" : "#404040" }} />
          {onRejectLoToggle && pct > 0 && (
            <div className="absolute top-[3px] h-2 rounded-sm cursor-pointer z-[1]"
              style={{ left: 0, width: `${pct}%`, background: rejectLo ? "rgba(239,68,68,0.25)" : "rgba(34,211,238,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onRejectLoToggle(); }} />
          )}
          {onRejectHiToggle && pct < 100 && (
            <div className="absolute top-[3px] h-2 rounded-sm cursor-pointer z-[1]"
              style={{ left: `${pct}%`, width: `${100 - pct}%`, background: rejectHi ? "rgba(239,68,68,0.25)" : "rgba(34,211,238,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onRejectHiToggle(); }} />
          )}
          <div tabIndex={disabled ? -1 : 0} role="slider"
            aria-valuemin={min} aria-valuemax={max} aria-valuenow={num}
            className="absolute top-0 w-3.5 h-3.5 rounded-full -translate-x-1/2 cursor-pointer focus:outline-none"
            style={{ left: `${pct}%`, background: disabled ? "#444" : focused ? "#10b981" : "#e6e6e6", zIndex: 2 }}
            onMouseDown={disabled ? undefined : startDrag}
            onKeyDown={disabled ? undefined : handleKey}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(max)}</span>
      </div>
      <div className="text-xs font-mono text-center mt-0.5 invisible" aria-hidden="true">&nbsp;</div>
    </div>
  );
}

/** Bundled value for ZoneSlider. */
export interface ZoneValue {
  value: number;
  leftAllow: boolean;   // false = reject (red), true = allow (cyan)
  rightAllow: boolean;
}

/** Parse a JSON ZoneValue string (e.g. {"value":21,"leftAllow":false,"rightAllow":true}),
 *  falling back to a default if missing or malformed. */
export function parseZone(json: string | undefined, fallback: ZoneValue): ZoneValue {
  if (json) {
    try {
      const o = JSON.parse(json);
      if (o && typeof o.value === "number" && typeof o.leftAllow === "boolean" && typeof o.rightAllow === "boolean")
        return { value: o.value, leftAllow: o.leftAllow, rightAllow: o.rightAllow };
    } catch {}
  }
  return fallback;
}

/** Single-thumb 1D slider with clickable accept/reject zones on either side of the thumb.
 *  Same visual language as CurveSlider (red reject / cyan allow), bundled value+zones into one prop.
 *  Click a zone (without ⌘/Ctrl) to toggle accept/reject. Drag the thumb to set the value. */
export function ZoneSlider({ min, max, step, value, onChange, fmt, disabled, trackCls }: {
  min: number; max: number; step: number;
  value: ZoneValue;
  onChange: (v: ZoneValue) => void;
  fmt?: (v: string) => string;
  disabled?: boolean;
  trackCls?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  const decimals = step.toString().split(".")[1]?.length ?? 0;
  const snap = (v: number) => Number((Math.round(v / step) * step).toFixed(decimals));

  const num = Math.max(min, Math.min(max, isNaN(value.value) ? min : value.value));

  const valFromX = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return snap(min + pct * (max - min));
  };

  const pctFor = (v: number) => ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * 100;

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).focus();
    const onMove = (ev: MouseEvent) => onChange({ ...value, value: valFromX(ev.clientX) });
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") delta = step;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") delta = -step;
    else return;
    e.preventDefault();
    const v = snap(num + delta);
    if (v >= min && v <= max) onChange({ ...value, value: v });
  };

  const f = (v: number) => fmt ? fmt(String(v)) : String(v);
  const pct = pctFor(num);
  const TRACK_H = 7;

  return (
    <div className={`pb-px ${trackCls || "flex-1 min-w-[3rem]"}`}>
      <div className={`text-[11px] font-mono text-center -mb-px ${disabled ? "text-foreground/30" : "text-foreground/70"}`}>{f(num)}</div>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(min)}</span>
        <div ref={trackRef} className={`relative flex-1 ${disabled ? "pointer-events-none" : ""}`} style={{ height: TRACK_H }}>
          <div className="absolute inset-0 rounded-sm" style={{ background: disabled ? "#262626" : "#404040" }} />
          {pct > 0 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[1] rounded-l-sm"
              style={{ left: 0, width: `${pct}%`, background: value.leftAllow ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onChange({ ...value, leftAllow: !value.leftAllow }); }} />
          )}
          {pct < 100 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[1] rounded-r-sm"
              style={{ left: `${pct}%`, width: `${100 - pct}%`, background: value.rightAllow ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onChange({ ...value, rightAllow: !value.rightAllow }); }} />
          )}
          <div tabIndex={disabled ? -1 : 0} role="slider"
            aria-valuemin={min} aria-valuemax={max} aria-valuenow={num}
            className="absolute w-3.5 h-3.5 rounded-full -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none z-[2]"
            style={{ left: `${pct}%`, top: "50%", background: disabled ? "#444" : focused ? "#10b981" : "#e6e6e6" }}
            onMouseDown={disabled ? undefined : startDrag}
            onKeyDown={disabled ? undefined : handleKey}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(max)}</span>
      </div>
      <div className="text-[11px] font-mono text-center -mt-px invisible" aria-hidden="true">&nbsp;</div>
    </div>
  );
}

/** Bundled value for ZoneRangeSlider. */
export interface ZoneRangeValue {
  lo: number;
  hi: number;
  leftAllow: boolean;   // false = reject (red), true = allow (cyan)
  rightAllow: boolean;
}

/** Parse a JSON ZoneRangeValue string, falling back to a default if missing or malformed. */
export function parseZoneRange(json: string | undefined, fallback: ZoneRangeValue): ZoneRangeValue {
  if (json) {
    try {
      const o = JSON.parse(json);
      if (o && typeof o.lo === "number" && typeof o.hi === "number"
          && typeof o.leftAllow === "boolean" && typeof o.rightAllow === "boolean") {
        const [lo, hi] = o.lo <= o.hi ? [o.lo, o.hi] : [o.hi, o.lo];
        return { lo, hi, leftAllow: o.leftAllow, rightAllow: o.rightAllow };
      }
    } catch {}
  }
  return fallback;
}

/** Two-thumb 1D range slider: rating = max inside [lo, hi], min outside, with clickable
 *  accept/reject zones outside the range. Same visual language as CurveSlider/ZoneSlider.
 *  Drag a thumb to move it; click an outer zone (without ⌘/Ctrl) to toggle accept/reject. */
export function ZoneRangeSlider({ min, max, step, value, onChange, fmt, disabled, trackCls }: {
  min: number; max: number; step: number;
  value: ZoneRangeValue;
  onChange: (v: ZoneRangeValue) => void;
  fmt?: (v: string) => string;
  disabled?: boolean;
  trackCls?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);

  const decimals = step.toString().split(".")[1]?.length ?? 0;
  const snap = (v: number) => Number((Math.round(v / step) * step).toFixed(decimals));

  const clamp = (v: number) => Math.max(min, Math.min(max, isNaN(v) ? min : v));
  const lo = clamp(value.lo);
  const hi = clamp(value.hi < value.lo ? value.lo : value.hi);

  const valFromX = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return snap(min + pct * (max - min));
  };

  const pctFor = (v: number) => ((clamp(v) - min) / (max - min)) * 100;

  const startDrag = (i: 0 | 1) => (e: React.MouseEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).focus();
    const onMove = (ev: MouseEvent) => {
      const v = valFromX(ev.clientX);
      if (i === 0) onChange({ ...value, lo: Math.min(v, hi) });
      else onChange({ ...value, hi: Math.max(v, lo) });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleKey = (i: 0 | 1) => (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") delta = step;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") delta = -step;
    else return;
    e.preventDefault();
    if (i === 0) {
      const v = snap(lo + delta);
      if (v >= min && v <= hi) onChange({ ...value, lo: v });
    } else {
      const v = snap(hi + delta);
      if (v >= lo && v <= max) onChange({ ...value, hi: v });
    }
  };

  const f = (v: number) => fmt ? fmt(String(v)) : String(v);
  const loPct = pctFor(lo);
  const hiPct = pctFor(hi);
  const TRACK_H = 7;

  return (
    <div className={`pb-px ${trackCls || "flex-1 min-w-[3rem]"}`}>
      <div className={`text-[11px] font-mono text-center -mb-px ${disabled ? "text-foreground/30" : "text-foreground/70"}`}>{f(lo)} – {f(hi)}</div>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(min)}</span>
        <div ref={trackRef} className={`relative flex-1 ${disabled ? "pointer-events-none" : ""}`} style={{ height: TRACK_H }}>
          <div className="absolute inset-0 rounded-sm" style={{ background: disabled ? "#262626" : "#404040" }} />
          {loPct > 0 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[1] rounded-l-sm"
              style={{ left: 0, width: `${loPct}%`, background: value.leftAllow ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onChange({ ...value, leftAllow: !value.leftAllow }); }} />
          )}
          {hiPct < 100 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[1] rounded-r-sm"
              style={{ left: `${hiPct}%`, width: `${100 - hiPct}%`, background: value.rightAllow ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { e.stopPropagation(); onChange({ ...value, rightAllow: !value.rightAllow }); }} />
          )}
          <div className="absolute top-0 bottom-0 pointer-events-none z-[1]"
            style={{ left: `${loPct}%`, width: `${hiPct - loPct}%`, background: "rgba(34,211,238,0.30)" }} />
          {[0, 1].map(i => {
            const v = i === 0 ? lo : hi;
            const p = i === 0 ? loPct : hiPct;
            return (
              <div key={i} tabIndex={disabled ? -1 : 0} role="slider"
                aria-valuemin={i === 0 ? min : lo} aria-valuemax={i === 0 ? hi : max} aria-valuenow={v}
                className="absolute w-3.5 h-3.5 rounded-full -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none z-[2]"
                style={{ left: `${p}%`, top: "50%", background: disabled ? "#444" : focusedIdx === i ? "#10b981" : "#e6e6e6" }}
                onMouseDown={disabled ? undefined : startDrag(i as 0 | 1)}
                onKeyDown={disabled ? undefined : handleKey(i as 0 | 1)}
                onFocus={() => setFocusedIdx(i)}
                onBlur={() => setFocusedIdx(null)}
              />
            );
          })}
        </div>
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(max)}</span>
      </div>
      <div className="text-[11px] font-mono text-center -mt-px invisible" aria-hidden="true">&nbsp;</div>
    </div>
  );
}

/** 2D Curve Slider: each thumb has an X (parameter value) and Y (rating 0-100)
 *  creating an arbitrary piecewise-linear rating curve.
 *  Left/right zones beyond outermost thumbs output a constant (0 or 100) toggled by clicking. */
export function CurveSlider({ min, max, step, points, onChange, leftY, rightY, onLeftYToggle, onRightYToggle, maxPoints = 8, minPoints = 1, yStep = 5, fmt, disabled, trackCls }: {
  min: number; max: number; step: number;
  points: [number, number][];
  onChange: (points: [number, number][]) => void;
  leftY: number; rightY: number;
  onLeftYToggle: () => void; onRightYToggle: () => void;
  maxPoints?: number; minPoints?: number;
  yStep?: number;
  fmt?: (v: string) => string;
  disabled?: boolean;
  trackCls?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);

  const decimals = step.toString().split(".")[1]?.length ?? 0;
  const snap = (v: number) => Number((Math.round(v / step) * step).toFixed(decimals));
  const snapY = (v: number) => Math.max(0, Math.min(100, Math.round(v / yStep) * yStep));

  const TRACK_H = 40;

  const pctFor = (v: number) => ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * 100;
  const yToSvg = (y: number) => 100 - y;

  const valFromClient = (clientX: number, clientY: number): [number, number] => {
    const rect = trackRef.current!.getBoundingClientRect();
    const xPct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const yPct = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
    return [snap(min + xPct * (max - min)), snapY(yPct * 100)];
  };

  // Effective y for the left/right zones:
  //   reject (negative) → 0 (so the reject overlay still reads the red tint; the fill curve is flat at 0)
  //   allow (>= 0) → extend the outermost thumb's y-value flat into the zone
  const leftZoneY = leftY < 0 ? 0 : (points.length > 0 ? points[0][1] : 0);
  const rightZoneY = rightY < 0 ? 0 : (points.length > 0 ? points[points.length - 1][1] : 0);

  const buildPolyPoints = (): string => {
    const pts: string[] = [`0,100`, `0,${yToSvg(leftZoneY)}`];
    if (points.length > 0) {
      pts.push(`${pctFor(points[0][0])},${yToSvg(leftZoneY)}`);
      for (const [x, y] of points) pts.push(`${pctFor(x)},${yToSvg(y)}`);
      pts.push(`${pctFor(points[points.length - 1][0])},${yToSvg(rightZoneY)}`);
    }
    pts.push(`100,${yToSvg(rightZoneY)}`, `100,100`);
    return pts.join(" ");
  };

  const buildLinePoints = (): string => {
    const pts: string[] = [`0,${yToSvg(leftZoneY)}`];
    if (points.length > 0) {
      pts.push(`${pctFor(points[0][0])},${yToSvg(leftZoneY)}`);
      for (const [x, y] of points) pts.push(`${pctFor(x)},${yToSvg(y)}`);
      pts.push(`${pctFor(points[points.length - 1][0])},${yToSvg(rightZoneY)}`);
    }
    pts.push(`100,${yToSvg(rightZoneY)}`);
    return pts.join(" ");
  };

  const startDrag = (i: number) => (e: React.MouseEvent) => {
    if (e.altKey && points.length > minPoints) {
      e.preventDefault();
      e.stopPropagation();
      const next = [...points];
      next.splice(i, 1);
      onChange(next);
      return;
    }
    e.preventDefault();
    (e.target as HTMLElement).focus();
    const loLimit = i > 0 ? points[i - 1][0] : min;
    const hiLimit = i < points.length - 1 ? points[i + 1][0] : max;
    const onMove = (ev: MouseEvent) => {
      const [xVal, yVal] = valFromClient(ev.clientX, ev.clientY);
      const clampedX = Math.max(loLimit, Math.min(hiLimit, xVal));
      const next = points.map(p => [...p] as [number, number]);
      next[i] = [clampedX, yVal];
      onChange(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleKey = (i: number) => (e: React.KeyboardEvent) => {
    let dx = 0, dy = 0;
    if (e.key === "ArrowRight") dx = step;
    else if (e.key === "ArrowLeft") dx = -step;
    else if (e.key === "ArrowUp") dy = yStep;
    else if (e.key === "ArrowDown") dy = -yStep;
    else return;
    e.preventDefault();
    const loLimit = i > 0 ? points[i - 1][0] : min;
    const hiLimit = i < points.length - 1 ? points[i + 1][0] : max;
    const newX = snap(points[i][0] + dx);
    const newY = snapY(points[i][1] + dy);
    if (newX >= loLimit && newX <= hiLimit) {
      const next = points.map(p => [...p] as [number, number]);
      next[i] = [newX, newY];
      onChange(next);
    }
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!(e.metaKey || e.ctrlKey) || points.length >= maxPoints) return;
    const [xVal, yVal] = valFromClient(e.clientX, e.clientY);
    if (points.some(p => Math.abs(p[0] - xVal) < step * 0.5)) return;
    onChange(([...points, [xVal, yVal]] as [number, number][]).sort((a, b) => a[0] - b[0]));
  };

  const f = (v: number) => fmt ? fmt(String(v)) : String(v);
  const label = points.map(([x, y]) => `${f(x)}→${y}`).join("  ");
  const firstPct = points.length > 0 ? pctFor(points[0][0]) : 100;
  const lastPct = points.length > 0 ? pctFor(points[points.length - 1][0]) : 0;

  return (
    <div className={`pb-px ${trackCls || "flex-1 min-w-[3rem]"}`}>
      <div className={`text-[11px] font-mono text-center mb-[4px] ${disabled ? "text-foreground/30" : "text-foreground/70"}`}>{label}</div>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(min)}</span>
        <div ref={trackRef} className={`relative flex-1 ${disabled ? "pointer-events-none" : ""}`} style={{ height: TRACK_H }}
          onClick={disabled ? undefined : handleTrackClick}>
          {/* Background */}
          <div className="absolute inset-0 rounded-sm" style={{ background: disabled ? "#262626" : "#404040" }} />
          {/* Grid lines */}
          {[0, 50, 100].map(y => (
            <div key={y} className="absolute left-0 right-0 pointer-events-none" style={{ top: `${100 - y}%`, height: 1, background: "rgba(255,255,255,0.08)" }} />
          ))}
          {/* Y-axis labels */}
          <span className="absolute text-[8px] text-white/30 pointer-events-none leading-none" style={{ left: 2, top: 1 }}>100</span>
          <span className="absolute text-[8px] text-white/30 pointer-events-none leading-none" style={{ left: 2, top: "50%", transform: "translateY(-50%)" }}>50</span>
          <span className="absolute text-[8px] text-white/30 pointer-events-none leading-none" style={{ left: 2, bottom: 1 }}>0</span>
          {/* SVG curve fill + line */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-[1]" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points={buildPolyPoints()} fill="#22d3ee" opacity="0.3" />
            <polyline points={buildLinePoints()} fill="none" stroke="#22d3ee" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </svg>
          {/* Left zone clickable: -1=reject(red), 0=allow-zero(cyan) */}
          {firstPct > 0 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[2] rounded-l-sm"
              style={{ left: 0, width: `${firstPct}%`, background: leftY >= 0 ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.stopPropagation(); onLeftYToggle(); }} />
          )}
          {/* Right zone clickable */}
          {lastPct < 100 && (
            <div className="absolute top-0 bottom-0 cursor-pointer z-[2] rounded-r-sm"
              style={{ left: `${lastPct}%`, width: `${100 - lastPct}%`, background: rightY >= 0 ? "rgba(34,211,238,0.12)" : "rgba(220,0,0,0.25)" }}
              onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.stopPropagation(); onRightYToggle(); }} />
          )}
          {/* Thumbs */}
          {points.map(([x, y], i) => (
            <div key={i} tabIndex={disabled ? -1 : 0} role="slider"
              aria-valuenow={x}
              className="absolute w-3 h-3 rounded-full -translate-x-1/2 -translate-y-1/2 cursor-pointer focus:outline-none z-[3]"
              style={{ left: `${pctFor(x)}%`, top: `${100 - y}%`, background: disabled ? "#444" : focusedIdx === i ? "#10b981" : "#e6e6e6" }}
              onMouseDown={disabled ? undefined : startDrag(i)}
              onKeyDown={disabled ? undefined : handleKey(i)}
              onFocus={() => setFocusedIdx(i)}
              onBlur={() => setFocusedIdx(null)}
            />
          ))}
        </div>
        <span className={`text-xs font-mono shrink-0 ${disabled ? "text-foreground/30" : "text-foreground"}`}>{f(max)}</span>
      </div>
      <div className="text-xs font-mono text-center mt-0.5 invisible" aria-hidden="true">&nbsp;</div>
    </div>
  );
}
